import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Loader2, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { PROGRAMS } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ApplyModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", program: "Private Pilot Licence (PPL)", goal: "career", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/applications`, form);
      setDone(true);
    } catch (_) { setLoading(false); }
  };

  const close = () => { onClose(); setTimeout(() => { setDone(false); setLoading(false); setForm({ name: "", email: "", phone: "", program: "Private Pilot Licence (PPL)", goal: "career", message: "" }); }, 300); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="apply-modal">
          <div className="absolute inset-0 bg-[#0A1128]/80 backdrop-blur-sm" onClick={close} />
          <motion.div initial={{ opacity: 0, y: 40, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-lg bg-white rounded-3xl overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="bg-navy p-8 relative">
              <button onClick={close} data-testid="apply-close" className="absolute top-5 right-5 text-white/60 hover:text-white"><X size={22} /></button>
              <span className="overline text-accent-2">Start Your Journey</span>
              <h3 className="mt-3 font-display text-3xl font-light text-white">Apply to Eagle 1</h3>
              <p className="mt-2 text-white/55 text-sm">Book a discovery flight or apply for training — we'll be in touch within one business day.</p>
            </div>

            {done ? (
              <div className="p-10 text-center" data-testid="apply-success">
                <CheckCircle2 className="mx-auto text-accent" size={56} />
                <h4 className="mt-5 font-display text-2xl text-[#0A1128]">Application received!</h4>
                <p className="mt-2 text-[#6B7280]">Thank you, {form.name || "future pilot"}. Our admissions team will contact you shortly.</p>
                <button onClick={close} className="mt-6 btn-primary px-8 py-3">Done</button>
              </div>
            ) : (
              <form onSubmit={submit} className="p-8 space-y-4">
                <In label="Full name" value={form.name} onChange={set("name")} testid="apply-name" required />
                <div className="grid sm:grid-cols-2 gap-4">
                  <In label="Email" type="email" value={form.email} onChange={set("email")} testid="apply-email" required />
                  <In label="Phone" value={form.phone} onChange={set("phone")} testid="apply-phone" />
                </div>
                <div>
                  <label className="text-[#6B7280] text-xs uppercase tracking-wide">Programme of interest</label>
                  <select value={form.program} onChange={set("program")} data-testid="apply-program"
                    className="mt-1.5 w-full bg-[#F8F9FA] border border-[#E5E7EB] rounded-xl px-4 py-3 text-[#0A1128] focus:border-gold focus:outline-none">
                    {PROGRAMS.map((p) => <option key={p.key}>{p.title}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[#6B7280] text-xs uppercase tracking-wide">Message (optional)</label>
                  <textarea rows={3} value={form.message} onChange={set("message")} data-testid="apply-message"
                    className="mt-1.5 w-full bg-[#F8F9FA] border border-[#E5E7EB] rounded-xl px-4 py-3 text-[#0A1128] focus:border-gold focus:outline-none resize-none" />
                </div>
                <button type="submit" disabled={loading} data-testid="apply-submit"
                  className="w-full btn-primary py-4 inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />} {loading ? "Submitting..." : "Submit Application"}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const In = ({ label, value, onChange, type = "text", testid, required }) => (
  <div>
    <label className="text-[#6B7280] text-xs uppercase tracking-wide">{label}</label>
    <input type={type} value={value} onChange={onChange} data-testid={testid} required={required}
      className="mt-1.5 w-full bg-[#F8F9FA] border border-[#E5E7EB] rounded-xl px-4 py-3 text-[#0A1128] focus:border-gold focus:outline-none" />
  </div>
);
